import { DocumentPayloadMap, OrderWorkProofDisplayStatus } from 'bff'
import { formatInTimeZone } from 'date-fns-tz'
import { translate } from 'i11n'
import { DEFAULT_TIMEZONE } from 'piramid-constants'
import QRCode from 'react-qr-code'
import {
  buildVehicleLabel,
  formatCurrencyAmount,
  formatTaxPercent,
} from 'utils'
import { Brandmark } from '../../components/Brandmark'
import { DocumentPage } from '../../components/DocumentPage'
import { LabeledRow } from '../../components/LabeledRow'
import { SignatureBlock } from '../../components/SignatureBlock'
import { StaticTable, StaticTableColumn } from '../../components/StaticTable'
import { cn } from '../../lib/cn'

type OrderReportProps = DocumentPayloadMap['order_report']

type OrderReportItem = OrderReportProps['items'][number]

type OrderWorkProof = NonNullable<OrderReportProps['work_proof']>

/**
 * Orden de trabajo — variante genérica, usada por todos los tenants.
 *
 * Diseño tomado de `docs/gamma-pdf.pdf`: formulario impreso, austero, con
 * bloques separados por reglas y un espacio final para firmar a mano. Los datos
 * vienen normalizados del resolver: acá no se decide nada de negocio.
 */
const OrderReportDefault = ({
  meta,
  order,
  parties,
  vehicle,
  items,
  summary,
  work_proof,
}: OrderReportProps) => {
  const money = (value: string | null) =>
    value === null ? null : formatCurrencyAmount(value, summary.currency)

  // El límite cuenta aunque sea null: se imprime como "sin límite".
  const hasSummary =
    summary.total_amount !== null ||
    summary.amount_to_charge !== null ||
    items.length > 0

  const columns: StaticTableColumn<OrderReportItem>[] = [
    {
      key: 'quantity',
      header: translate('document.order_report.items.quantity'),
      align: 'center',
      className: 'w-[14mm]',
      render: (item) => item.quantity,
    },
    {
      key: 'description',
      // Sin columna de código: para las líneas de catálogo el SKU ya viene
      // embebido al principio de la descripción.
      header: translate('document.order_report.items.description'),
      render: (item) => item.description,
    },
    {
      key: 'unit_price',
      header: translate('document.order_report.items.unit_price'),
      align: 'right',
      className: 'w-[28mm]',
      render: (item) => formatCurrencyAmount(item.unit_price, summary.currency),
    },
    {
      key: 'subtotal',
      header: translate('document.order_report.items.subtotal'),
      align: 'right',
      className: 'w-[28mm]',
      render: (item) => formatCurrencyAmount(item.subtotal, summary.currency),
    },
  ]

  return (
    <DocumentPage className='flex flex-col gap-4'>
      <header className='flex items-center justify-between gap-6'>
        <div className='flex items-center gap-4'>
          {meta.branding.logo_data_uri && (
            <Brandmark branding={meta.branding} className='h-[18mm]' />
          )}

          <div className='leading-tight'>
            <p className='text-[12pt] font-bold'>
              {meta.branding.company_name}
            </p>

            {meta.branding.tax_id && (
              <p className='text-[9pt] text-zinc-600'>
                {translate('document.tax_id')}: {meta.branding.tax_id}
              </p>
            )}
          </div>
        </div>

        <div className='shrink-0 text-right'>
          <p className='text-[16pt] font-bold'>
            {translate('document.order_report.number')}{' '}
            <span className='font-mono'>{order.ref_id}</span>
          </p>

          {order.external_claim_id && (
            <p className='text-[10pt] text-zinc-600'>
              {/* {translate('document.order_report.claim_number')}{' '} */}
              Reclamo externo{' '}
              <span className='font-mono'>{order.external_claim_id}</span>
            </p>
          )}
        </div>
      </header>

      <hr className='border-t-2 border-zinc-900' />

      {/* The title spans the full width and the two columns hang below. Without
          work proof the grid is not mounted and a report order looks as ever. */}
      <section className='flex flex-col gap-1'>
        <h1 className='mb-2 text-[16pt] font-bold uppercase'>
          {translate('document.order_report.name')}{' '}
          <span className='font-mono'>{order.ref_id}</span>
        </h1>

        {work_proof ? (
          <div className='grid grid-cols-2 gap-6'>
            <div className='flex min-w-0 flex-col gap-1'>
              <OrderFields order={order} parties={parties} vehicle={vehicle} />
            </div>

            <WorkProofBlock workProof={work_proof} />
          </div>
        ) : (
          <OrderFields order={order} parties={parties} vehicle={vehicle} />
        )}
      </section>

      {items.length > 0 && (
        <section className='flex flex-col gap-1 my-4'>
          <h2 className='font-bold uppercase'>
            {translate('document.order_report.details_title')}
          </h2>

          <StaticTable
            columns={columns}
            rows={items}
            getRowKey={(item, index) => `${item.description}-${index}`}
          />
        </section>
      )}

      {hasSummary && (
        <section className='flex flex-col gap-1 text-right'>
          <SummaryRow label='Importe' value={money(summary.total_amount)} />
          <SummaryRow
            label='Cobertura'
            value={
              money(summary.coverage_amount)
                ? `- ${money(summary.coverage_amount)}`
                : 'No aplica'
            }
          />
          <SummaryRow
            label='Importe neto'
            value={money(summary.amount_to_charge)}
          />
          <SummaryRow
            label={`IVA (${formatTaxPercent(
              Number(summary.taxes_percent ?? 0),
            )}%)`}
            value={money(summary.taxes_amount ?? null)}
          />
          <SummaryRow
            isBold
            label='Total'
            value={money(summary.total_with_taxes ?? null)}
          />
        </section>
      )}

      {/* La marca de generación no va acá: se repite en cada página desde el
          pie de Chromium (ver `withFooterNote`). */}
      <div className='mt-20'>
        <SignatureBlock
          fields={[
            translate('document.order_report.signature'),
            translate('document.order_report.clarification'),
            translate('document.order_report.identification'),
            translate('document.order_report.date'),
          ]}
        />
      </div>
    </DocumentPage>
  )
}

/**
 * Body of the initial information. It lives apart so both branches —with and
 * without work proof— share exactly the same fields.
 */
const OrderFields = ({
  order,
  parties,
  vehicle,
}: Pick<OrderReportProps, 'order' | 'parties' | 'vehicle'>) => {
  const vehicleDescription = buildVehicleLabel(vehicle)

  return (
    <>
      <LabeledRow label={translate('document.order_report.created_at')}>
        {formatInTimeZone(
          new Date(order.created_at),
          DEFAULT_TIMEZONE,
          'dd-MM-yy HH:mm',
        )}
      </LabeledRow>

      <LabeledRow label={translate('document.order_report.insurer')}>
        {parties.insurer_name}
      </LabeledRow>

      <LabeledRow label={translate('document.order_report.provider')}>
        {parties.provider_name}
      </LabeledRow>

      {parties.insured.name && (
        <LabeledRow
          label={translate('document.order_report.insured')}
          className='mt-3'
        >
          {parties.insured.name}
          {parties.insured.identification &&
            ` (${parties.insured.identification})`}
        </LabeledRow>
      )}

      {parties.insured.phone && (
        <LabeledRow label={translate('document.order_report.phone')}>
          {parties.insured.phone}
        </LabeledRow>
      )}

      {parties.insured.email && (
        <LabeledRow label={translate('document.order_report.email')}>
          {parties.insured.email}
        </LabeledRow>
      )}

      {/* {parties.insured.address && (
        <LabeledRow label={translate('document.order_report.address')}>
          {parties.insured.address}
        </LabeledRow>
      )} */}

      {vehicle && (
        <>
          {/* Puede haber patente sin marca/modelo: el fallback la toma del
                claim cuando no hay daño material cargado. */}
          {vehicleDescription && (
            <LabeledRow
              label={translate('document.order_report.vehicle')}
              className='mt-3'
            >
              {vehicleDescription}
            </LabeledRow>
          )}

          {vehicle.license_plate && (
            <LabeledRow
              label={translate('document.order_report.license_plate')}
              className={vehicleDescription ? undefined : 'mt-3'}
            >
              {vehicle.license_plate}
            </LabeledRow>
          )}
        </>
      )}

      {order.description && (
        <LabeledRow
          label={translate('document.order_report.observation')}
          className='mt-3'
        >
          {order.description}
        </LabeledRow>
      )}
    </>
  )
}

/**
 * Right column of the inspection mechanism: the QR while the capture can still
 * advance, otherwise the status, so "not yet" reads apart from "already done".
 */
const WorkProofBlock = ({ workProof }: { workProof: OrderWorkProof }) => (
  // The cell stretches to the data column height; the content centers inside.
  <aside className='flex break-inside-avoid flex-col items-center justify-center text-center pl-8'>
    <p className='mb-2 text-[9pt] font-bold uppercase'>
      {translate('document.order_report.work_proof.title')}
    </p>

    {workProof.capture_link ? (
      <div className='max-w-[50mm]'>
        {/* Medium error correction: the panel's low level is fine on screen,
            but paper gets folded and stained. */}
        <QRCode
          value={workProof.capture_link}
          level='M'
          className='h-[50mm] w-[50mm]'
        />

        <p className='mt-2 text-[8pt] leading-tight text-zinc-600'>
          {translate('document.order_report.work_proof.qr_hint')}
        </p>

        <p className='mt-1 text-[8pt] font-bold uppercase leading-tight text-zinc-600'>
          {translate('document.order_report.work_proof.private_notice')}
        </p>
      </div>
    ) : (
      // Same footprint as the QR so both variants take up the same space.
      <div className='flex h-[50mm] w-[50mm] flex-col items-center justify-center rounded-sm border border-dashed border-zinc-400 px-3'>
        <p className='text-[9pt] font-bold leading-tight'>
          {translate('document.order_report.work_proof.unavailable_title')}
        </p>

        <p className='mt-1 text-[8pt] leading-tight text-zinc-600'>
          {workProof.status
            ? WORK_PROOF_STATUS_LABEL[workProof.status]()
            : translate('document.order_report.work_proof.not_created')}
        </p>
      </div>
    )}
  </aside>
)

// Reuses the apps panel vocabulary instead of duplicating copy. Complete
// record: a new display status breaks the build right here.
const WORK_PROOF_STATUS_LABEL: Record<
  OrderWorkProofDisplayStatus,
  () => string
> = {
  pending_capture: () => translate('orders.workProof.status.pending_capture'),
  capturing: () => translate('orders.workProof.status.capturing'),
  processing: () => translate('orders.workProof.status.processing'),
  approved: () => translate('orders.workProof.status.approved'),
  disapproved: () => translate('orders.workProof.status.disapproved'),
  error: () => translate('orders.workProof.status.error'),
  blocked: () => translate('orders.workProof.status.blocked'),
}

const SummaryRow = ({
  label,
  value,
  isBold = false,
}: {
  label: string
  value: string | null
  isBold?: boolean
}) =>
  value === null ? null : (
    <p className='break-inside-avoid'>
      <span className='font-semibold'>{label}:</span>{' '}
      <span
        className={cn(
          'inline-block w-[35mm] text-right tabular-nums',
          isBold && 'font-bold',
        )}
      >
        {value}
      </span>
    </p>
  )

export default OrderReportDefault
